import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchDownloads, formatSize } from "../content/downloads";
import type { DownloadsResponse, DownloadFile, DistributionDownload } from "../content/downloads";
import {
  PlatformCard,
  SystemRequirements,
  InstallationNotes,
  CloudEditionBanner,
} from "../components/downloads";

type Platform = "windows" | "macos" | "linux" | "unknown";
type Arch = "arm64" | "x64" | "unknown";

interface DetectedClient {
  platform: Platform;
  arch: Arch;
}

/** User-Agent Client Hints API (not yet in TypeScript's DOM lib) */
interface NavigatorUAData {
  getHighEntropyValues(hints: string[]): Promise<{ architecture?: string }>;
}

/** Type-safe accessor for navigator.userAgentData using runtime narrowing */
function getNavigatorUAData(): NavigatorUAData | undefined {
  if ("userAgentData" in navigator) {
    return navigator.userAgentData as NavigatorUAData;
  }
  return undefined;
}

async function detectClient(): Promise<DetectedClient> {
  if (typeof navigator === "undefined") return { platform: "unknown", arch: "unknown" };

  const platform = navigator.platform.toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();

  let detectedPlatform: Platform = "unknown";
  if (platform.includes("win") || userAgent.includes("windows")) {
    detectedPlatform = "windows";
  } else if (
    platform.includes("mac") ||
    userAgent.includes("macintosh") ||
    userAgent.includes("mac os x")
  ) {
    detectedPlatform = "macos";
  } else if (platform.includes("linux") || userAgent.includes("linux")) {
    detectedPlatform = "linux";
  }

  // Detect architecture
  let arch: Arch = "x64";
  if (detectedPlatform === "macos") {
    if (platform.includes("arm")) {
      arch = "arm64";
    } else {
      const uaData = getNavigatorUAData();
      if (uaData?.getHighEntropyValues) {
        try {
          const hints = await uaData.getHighEntropyValues(["architecture"]);
          if (hints.architecture === "arm") {
            arch = "arm64";
          }
        } catch {
          // Permission denied or unsupported — fall back to x64
        }
      }
    }
  } else if (userAgent.includes("aarch64") || userAgent.includes("arm64")) {
    arch = "arm64";
  }

  return { platform: detectedPlatform, arch };
}

interface PlatformEntry {
  id: "windows" | "macos" | "linux";
  name: string;
  description: string;
  icon: "windows" | "apple" | "linux";
  version?: string;
  releaseDate?: string;
  files: DownloadFile[];
}

function usePlatformEntries(dist: DistributionDownload): PlatformEntry[] {
  const { t } = useTranslation();
  return [
    {
      id: "windows",
      name: t("downloads.platforms.windows.name"),
      description: t("downloads.platforms.windows.description"),
      icon: "windows",
      version: dist.platforms.windows?.version,
      releaseDate: dist.releaseDate || undefined,
      files: dist.platforms.windows?.files ?? [],
    },
    {
      id: "macos",
      name: t("downloads.platforms.macos.name"),
      description: t("downloads.platforms.macos.description"),
      icon: "apple",
      version: dist.platforms.macos?.version,
      releaseDate: dist.releaseDate || undefined,
      files: dist.platforms.macos?.files ?? [],
    },
    {
      id: "linux",
      name: t("downloads.platforms.linux.name"),
      description: t("downloads.platforms.linux.description"),
      icon: "linux",
      version: dist.platforms.linux?.version,
      releaseDate: dist.releaseDate || undefined,
      files: dist.platforms.linux?.files ?? [],
    },
  ];
}

function getAllFiles(data: DownloadsResponse): DownloadFile[] {
  const files: DownloadFile[] = [];
  for (const dist of data.distributions) {
    for (const platform of Object.values(dist.platforms)) {
      if (platform) files.push(...platform.files);
    }
  }
  return files;
}

/** Distribution section with platform cards inside */
function DistributionSection({
  dist,
  client,
}: {
  dist: DistributionDownload;
  client: DetectedClient;
}) {
  const platforms = usePlatformEntries(dist);

  return (
    <div className="mb-12">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-warm-50 mb-1">{dist.name}</h2>
        <p className="text-warm-400">{dist.description}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {platforms.map((platform) => (
          <PlatformCard
            key={platform.id}
            platform={platform}
            isHighlighted={client.platform === platform.id}
            preferredArch={client.platform === platform.id ? client.arch : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export function Downloads() {
  const { t } = useTranslation();
  const [client, setClient] = useState<DetectedClient>({ platform: "unknown", arch: "unknown" });
  const [data, setData] = useState<DownloadsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [checksumsExpanded, setChecksumsExpanded] = useState(true);

  useEffect(() => {
    detectClient().then(setClient);
  }, []);

  useEffect(() => {
    fetchDownloads()
      .then((response) => {
        setData(response);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load downloads");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-warm-500 border-t-blue-500 rounded-full animate-spin mb-4" />
          <p className="text-warm-400">{t('downloads.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center">
          <div className="p-3 rounded-full bg-red-500/10 inline-block mb-4">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-warm-50 mb-2">{t('downloads.error.title')}</h2>
          <p className="text-warm-400 mb-4">{error || t('downloads.error.fallback')}</p>
          <button
            onClick={() => {
              setLoading(true);
              setError(null);
              fetchDownloads()
                .then((response) => {
                  setData(response);
                  setError(null);
                })
                .catch((err) => {
                  setError(err instanceof Error ? err.message : "Failed to load downloads");
                })
                .finally(() => {
                  setLoading(false);
                });
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
          >
            {t('downloads.error.retry')}
          </button>
        </div>
      </div>
    );
  }

  const allFiles = getAllFiles(data);

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ backgroundColor: "#1a1816" }}>
      {/* Ambient radial gradients based on logotype colors */}
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] opacity-[0.07]"
        style={{
          background:
            "radial-gradient(ellipse at center, #EC4899 0%, #8B5CF6 40%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute top-[500px] -left-[200px] w-[800px] h-[800px] opacity-[0.04]"
        style={{
          background:
            "radial-gradient(ellipse at center, #F97316 0%, #EC4899 50%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute top-[400px] -right-[200px] w-[800px] h-[800px] opacity-[0.04]"
        style={{
          background:
            "radial-gradient(ellipse at center, #3B82F6 0%, #06B6D4 50%, transparent 70%)",
        }}
      />
      <div
        className="pointer-events-none absolute bottom-0 left-1/3 w-[600px] h-[400px] opacity-[0.03]"
        style={{
          background:
            "radial-gradient(ellipse at center, #8B5CF6 0%, #3B82F6 50%, transparent 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-warm-50 mb-4">
            {t('downloads.header.title')}
          </h1>
          <p className="text-warm-500">{t('downloads.header.subtitle')}</p>
        </div>

        <CloudEditionBanner />

        {data.distributions.map((dist) => (
          <DistributionSection key={dist.id} dist={dist} client={client} />
        ))}

        <div className="space-y-6">
          <InstallationNotes />
          <SystemRequirements />

          {allFiles.length > 0 && (
            <div className="bg-dark-card rounded-xl border border-dark-border overflow-hidden">
              <button
                onClick={() => setChecksumsExpanded(!checksumsExpanded)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-dark-hover transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-dark-bg">
                    <svg
                      className="w-5 h-5 text-warm-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-warm-50">{t('downloads.checksums.title')}</h3>
                    <p className="text-sm text-warm-400">
                      {t('downloads.checksums.subtitle')}
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-5 h-5 text-warm-500 transition-transform ${checksumsExpanded ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {checksumsExpanded && (
                <div className="border-t border-dark-border p-6 space-y-3">
                  {allFiles.map((file) => (
                    <div key={file.filename} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-warm-200">{file.filename}</span>
                        <span className="text-xs text-warm-500">{formatSize(file.size)}</span>
                      </div>
                      <code className="block text-xs text-warm-500 bg-dark-bg rounded px-3 py-2 font-mono break-all select-all">
                        {file.sha512}
                      </code>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
